/**
 * InteractiveTable — Reusable table with column resize, reorder, picker, and fit-to-screen.
 *
 * Props:
 *   tableId      — string, used for persisting column prefs (e.g. "po-listing")
 *   defaultCols  — array of { key, label, width, visible, sortField, align, fixed }
 *   data         — array of row objects
 *   renderCell   — (col, row) => ReactNode
 *   onRowClick   — (row) => void (optional)
 *   sortState    — { field, dir } (optional)
 *   onSort       — (field) => void (optional)
 *   rowKey       — string, key to use for React key (default 'id')
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import api from '../services/api';
import {
  ChevronDown, ChevronUp, ChevronsUpDown,
  Settings2, Eye, EyeOff, RotateCcw, GripVertical, Maximize2, Filter, X, Download,
} from 'lucide-react';

// Simple CSV export helper
function exportDataToCSV(data, filename) {
  if (!data?.length) return;
  const allKeys = new Set();
  data.forEach(r => Object.keys(r).forEach(k => {
    if (k !== '_id' && k !== '__v' && k !== '_version') allKeys.add(k);
  }));
  const keys = [...allKeys];
  const headers = keys.map(k => k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
  const rows = data.map(r => keys.map(k => {
    const val = r[k];
    if (val == null) return '';
    const str = typeof val === 'object' ? JSON.stringify(val) : String(val);
    return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str;
  }));
  const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${filename}.csv`; a.click();
  URL.revokeObjectURL(url);
}

export default function InteractiveTable({
  tableId, defaultCols, data, renderCell, onRowClick,
  sortState, onSort, rowKey = 'id', filterable = true,
  toolbarPortalId,
}) {
  const [columns, setColumns] = useState(defaultCols);
  const [colFilters, setColFilters] = useState({});
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [showAddresses, setShowAddresses] = useState(false);
  const hasAddressCols = useMemo(() => defaultCols.some(c => c.addressCol), [defaultCols]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [dropTarget, setDropTarget] = useState(null);
  const [pickerDragIdx, setPickerDragIdx] = useState(null);
  const tableContainerRef = useRef(null);
  const resizeRef = useRef({ active: false, colIdx: -1, startX: 0, startW: 0 });
  const dragRef = useRef({ dragging: false, fromKey: null });

  const prefModule = tableId;
  const prefKey = `${tableId}_main`;

  // Load prefs
  useEffect(() => {
    api.get(`/api/saved-reports/column-prefs/${prefModule}/${prefKey}`).then(r => {
      if (r.data?.columns?.length) {
        const savedMap = {};
        r.data.columns.forEach(sc => { savedMap[sc.key] = sc; });
        const merged = [];
        const savedKeys = r.data.columns.map(sc => sc.key);
        // Always emit fixed cols first (in their default order), regardless of saved order
        const fixedKeys = defaultCols.filter(d => d.fixed).map(d => d.key);
        for (const fk of fixedKeys) {
          const def = defaultCols.find(d => d.key === fk);
          merged.push({ ...def, visible: savedMap[fk]?.visible !== false, width: savedMap[fk]?.width || def.width });
        }
        for (const sk of savedKeys) {
          if (fixedKeys.includes(sk)) continue;
          const def = defaultCols.find(d => d.key === sk);
          if (def) merged.push({ ...def, visible: savedMap[sk].visible !== false, width: savedMap[sk].width || def.width });
        }
        for (const dc of defaultCols) {
          if (!savedKeys.includes(dc.key) && !fixedKeys.includes(dc.key)) merged.push({ ...dc });
        }
        setColumns(merged);
      }
    }).catch(() => {});
  }, [defaultCols, prefModule, prefKey]);

  const savePrefs = useCallback((cols) => {
    const toSave = cols.map(c => ({ key: c.key, visible: c.visible, width: c.width }));
    api.put(`/api/saved-reports/column-prefs/${prefModule}/${prefKey}`, { columns: toSave }).catch(() => {});
  }, [prefModule, prefKey]);

  const visibleCols = useMemo(() => columns.filter(c => {
    if (!c.visible) return false;
    if (c.addressCol && !showAddresses) return false;
    return true;
  }), [columns, showAddresses]);

  // ── Resize ──
  const onResizeStart = useCallback((e, ci) => {
    e.preventDefault(); e.stopPropagation();
    resizeRef.current = { active: true, colIdx: ci, startX: e.clientX, startW: visibleCols[ci].width };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [visibleCols]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!resizeRef.current.active) return;
      const { colIdx, startX, startW } = resizeRef.current;
      const newW = Math.max(50, startW + (e.clientX - startX));
      setColumns(prev => {
        const key = visibleCols[colIdx]?.key;
        if (!key) return prev;
        return prev.map(c => c.key === key ? { ...c, width: newW } : c);
      });
    };
    const onMouseUp = () => {
      if (resizeRef.current.active) {
        resizeRef.current.active = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        setColumns(prev => { savePrefs(prev); return prev; });
      }
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => { window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('mouseup', onMouseUp); };
  }, [visibleCols, savePrefs]);

  // ── Fit to screen ──
  const fitToScreen = useCallback(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const avail = el.clientWidth;
    const vis = columns.filter(c => c.visible);
    const total = vis.reduce((s, c) => s + c.width, 0);
    if (!total || !avail) return;
    const ratio = avail / total;
    setColumns(prev => {
      const updated = prev.map(c => c.visible ? { ...c, width: Math.max(50, Math.round(c.width * ratio)) } : c);
      savePrefs(updated);
      return updated;
    });
  }, [columns, savePrefs]);

  // ── Header drag reorder ──
  const onDragStart = useCallback((e, key) => {
    if (columns.find(c => c.key === key)?.fixed) { e.preventDefault(); return; }
    dragRef.current = { dragging: true, fromKey: key };
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', key);
    e.currentTarget.style.opacity = '0.5';
  }, [columns]);
  const onDragEnd = useCallback((e) => { e.currentTarget.style.opacity = '1'; dragRef.current.dragging = false; setDropTarget(null); }, []);
  const onDragOver = useCallback((e, key) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragRef.current.fromKey !== key) setDropTarget(key); }, []);
  const onDrop = useCallback((e, toKey) => {
    e.preventDefault();
    const fromKey = dragRef.current.fromKey;
    if (!fromKey || fromKey === toKey) { setDropTarget(null); return; }
    setColumns(prev => {
      const arr = [...prev];
      const fi = arr.findIndex(c => c.key === fromKey);
      const ti = arr.findIndex(c => c.key === toKey);
      if (fi < 0 || ti < 0) return prev;
      const [moved] = arr.splice(fi, 1);
      arr.splice(ti, 0, moved);
      savePrefs(arr);
      return arr;
    });
    setDropTarget(null);
  }, [savePrefs]);

  // ── Column picker ──
  const toggleVis = useCallback((key) => {
    setColumns(prev => { const u = prev.map(c => c.key === key ? { ...c, visible: !c.visible } : c); savePrefs(u); return u; });
  }, [savePrefs]);
  const resetCols = useCallback(() => { setColumns(defaultCols); savePrefs(defaultCols); }, [defaultCols, savePrefs]);

  const onPickerDragStart = (idx) => setPickerDragIdx(idx);
  const onPickerDragOver = (e, idx) => {
    e.preventDefault();
    if (pickerDragIdx === null || pickerDragIdx === idx) return;
    setColumns(prev => { const arr = [...prev]; const [m] = arr.splice(pickerDragIdx, 1); arr.splice(idx, 0, m); return arr; });
    setPickerDragIdx(idx);
  };
  const onPickerDragEnd = () => { setPickerDragIdx(null); setColumns(prev => { savePrefs(prev); return prev; }); };

  // ── Column Filters ──
  const activeFilterCount = useMemo(() => Object.values(colFilters).filter(v => v && v.trim()).length, [colFilters]);
  const setFilter = useCallback((key, val) => {
    setColFilters(prev => ({ ...prev, [key]: val }));
  }, []);
  const clearAllFilters = useCallback(() => { setColFilters({}); }, []);

  const filteredData = useMemo(() => {
    const active = Object.entries(colFilters).filter(([, v]) => v && v.trim());
    if (!active.length) return data;
    return data.filter(row => active.every(([key, val]) => {
      const cellVal = row[key];
      if (cellVal == null) return false;
      const str = String(cellVal).toLowerCase();
      const search = val.trim().toLowerCase();
      return str.includes(search);
    }));
  }, [data, colFilters]);

  // Portal target for toolbar
  const [portalTarget, setPortalTarget] = useState(null);
  useEffect(() => {
    if (!toolbarPortalId) return;
    const el = document.getElementById(toolbarPortalId);
    if (el) setPortalTarget(el);
    // Retry on next frame if DOM not ready yet
    const t = setTimeout(() => {
      const el2 = document.getElementById(toolbarPortalId);
      if (el2) setPortalTarget(el2);
    }, 0);
    return () => clearTimeout(t);
  }, [toolbarPortalId]);

  const toolbar = (
    <div className="flex items-center justify-end gap-1.5 mb-2" data-testid={`${tableId}-col-toolbar`}>
        {hasAddressCols && (
          <label className="flex items-center gap-1.5 mr-2 cursor-pointer select-none" data-testid={`${tableId}-show-addresses`}>
            <input type="checkbox" checked={showAddresses} onChange={e => setShowAddresses(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary" />
            <span className="text-xs font-medium text-muted-foreground">Show Addresses</span>
          </label>
        )}
        {filterable && (
          <Button size="sm" variant={filtersVisible ? "default" : "outline"} onClick={() => setFiltersVisible(!filtersVisible)}
            className={`h-8 px-2.5 gap-1 ${activeFilterCount ? 'ring-2 ring-primary/30' : ''}`} data-testid={`${tableId}-filter-toggle`} title="Toggle column filters">
            <Filter className="h-3.5 w-3.5" />
            <span className="text-xs">Filters{activeFilterCount ? ` (${activeFilterCount})` : ''}</span>
          </Button>
        )}
        {activeFilterCount > 0 && (
          <Button size="sm" variant="ghost" onClick={clearAllFilters} className="h-8 px-2 gap-1 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`${tableId}-clear-filters`} title="Clear all filters">
            <X className="h-3.5 w-3.5" /> <span className="text-xs">Clear</span>
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={fitToScreen} className="h-8 px-2.5 gap-1" data-testid={`${tableId}-fit-btn`} title="Fit to screen">
          <Maximize2 className="h-3.5 w-3.5" /> <span className="text-xs">Fit</span>
        </Button>
        <Button size="sm" variant="outline" onClick={() => exportDataToCSV(filteredData, tableId)}
          className="h-8 px-2.5 gap-1" data-testid={`${tableId}-export-csv`} title="Export to CSV (all fields)">
          <Download className="h-3.5 w-3.5" /> <span className="text-xs">CSV</span>
        </Button>
        <div className="relative">
          <Button size="sm" variant="outline" onClick={() => setPickerOpen(!pickerOpen)} className="h-8 px-2.5 gap-1" data-testid={`${tableId}-col-picker-btn`} title="Columns">
            <Settings2 className="h-3.5 w-3.5" /> <span className="text-xs">Columns</span>
          </Button>
          {pickerOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setPickerOpen(false)} />
              <div className="absolute right-0 top-9 z-50 w-72 bg-white border rounded-xl shadow-2xl p-3 max-h-[420px] overflow-auto" data-testid={`${tableId}-col-panel`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-slate-800">Configure Columns</span>
                  <button onClick={resetCols} className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5" data-testid={`${tableId}-col-reset`}>
                    <RotateCcw className="h-3 w-3" /> Reset
                  </button>
                </div>
                <p className="text-[10px] text-slate-500 mb-2">Toggle visibility. Drag to reorder.</p>
                <div className="space-y-1">
                  {columns.filter(c => !c.fixed).map((c) => {
                    const ri = columns.indexOf(c);
                    return (
                      <div key={c.key} draggable onDragStart={() => onPickerDragStart(ri)} onDragOver={(e) => onPickerDragOver(e, ri)} onDragEnd={onPickerDragEnd}
                        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-xs transition-colors ${c.visible ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'} ${pickerDragIdx === ri ? 'ring-2 ring-primary/30' : ''}`}>
                        <GripVertical className="h-3 w-3 text-slate-400 cursor-grab flex-shrink-0" />
                        <button onClick={() => toggleVis(c.key)} className="flex-shrink-0">
                          {c.visible ? <Eye className="h-3.5 w-3.5 text-emerald-600" /> : <EyeOff className="h-3.5 w-3.5 text-slate-400" />}
                        </button>
                        <span className={`flex-1 truncate ${c.visible ? 'text-slate-800' : 'text-slate-400 line-through'}`}>{c.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
  );

  return (
    <>
      {toolbarPortalId
        ? (portalTarget ? createPortal(toolbar, portalTarget) : null)
        : toolbar}

      {/* Table */}
      <div className="border rounded-lg overflow-x-auto" ref={tableContainerRef} data-testid={`${tableId}-table-container`}>
        <table className="text-sm" style={{ tableLayout: 'fixed', width: visibleCols.reduce((s, c) => s + c.width, 0) }} data-testid={`${tableId}-table`}>
          <thead>
            <tr className="border-b bg-muted/50">
              {visibleCols.map((col, ci) => {
                const isActive = sortState?.field === col.sortField;
                const dir = isActive ? sortState.dir : null;
                const isDT = dropTarget === col.key;
                return (
                  <th key={col.key} style={{ width: col.width, minWidth: 50, position: 'relative' }}
                    className={`h-10 px-3 text-left align-middle font-semibold text-[11px] uppercase tracking-wide text-muted-foreground select-none whitespace-nowrap ${col.align === 'right' ? 'text-right' : ''} ${isDT ? 'bg-primary/10 border-l-2 border-primary' : ''}`}
                    draggable={!col.fixed} onDragStart={e => onDragStart(e, col.key)} onDragEnd={onDragEnd} onDragOver={e => onDragOver(e, col.key)} onDrop={e => onDrop(e, col.key)}
                    data-testid={`col-header-${col.key}`}>
                    {col.sortField && onSort ? (
                      <button onClick={() => onSort(col.sortField)}
                        className={`flex items-center gap-1 hover:text-foreground transition-colors ${isActive ? 'text-primary' : ''} ${col.align === 'right' ? 'ml-auto' : ''}`}>
                        <span>{col.label}</span>
                        <span className="flex-shrink-0">
                          {!isActive && <ChevronsUpDown className="h-3 w-3 opacity-40" />}
                          {isActive && dir === 'asc' && <ChevronUp className="h-3 w-3" />}
                          {isActive && dir === 'desc' && <ChevronDown className="h-3 w-3" />}
                        </span>
                      </button>
                    ) : <span>{col.label}</span>}
                    {ci < visibleCols.length - 1 && (
                      <div className="absolute right-0 top-0 bottom-0 w-[5px] cursor-col-resize hover:bg-primary/30 active:bg-primary/50 z-10 group"
                        onMouseDown={e => onResizeStart(e, ci)} data-testid={`resize-${col.key}`}>
                        <div className="absolute right-[2px] top-2 bottom-2 w-[1px] bg-border group-hover:bg-primary/60 transition-colors" />
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
            {filterable && filtersVisible && (
              <tr className="border-b bg-slate-50/80">
                {visibleCols.map(col => (
                  <th key={`filter-${col.key}`} style={{ width: col.width, minWidth: 50 }} className="px-1.5 py-1.5">
                    <input
                      type="text"
                      value={colFilters[col.key] || ''}
                      onChange={e => setFilter(col.key, e.target.value)}
                      placeholder={`Filter...`}
                      className="w-full h-7 px-2 text-xs rounded border border-slate-200 bg-white focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40 placeholder:text-slate-300"
                      data-testid={`${tableId}-filter-${col.key}`}
                      onClick={e => e.stopPropagation()}
                    />
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {filteredData.length === 0 && (
              <tr><td colSpan={visibleCols.length} className="text-center py-8 text-sm text-muted-foreground">
                {activeFilterCount > 0 ? 'No records match the current filters' : 'No records'}
              </td></tr>
            )}
            {filteredData.map((row, ri) => (
              <tr key={row[rowKey] || ri}
                className={`border-b transition-colors hover:bg-muted/50 ${onRowClick ? 'cursor-pointer' : ''}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                data-testid={`${tableId}-row`}>
                {visibleCols.map(col => (
                  <td key={col.key} style={{ width: col.width, maxWidth: col.width, overflow: 'hidden' }}
                    className={`p-2 align-middle ${col.align === 'right' ? 'text-right' : ''}`}>
                    {renderCell(col, row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {activeFilterCount > 0 && (
        <div className="flex items-center gap-2 mt-1.5 px-1 text-xs text-muted-foreground" data-testid={`${tableId}-filter-status`}>
          <Filter className="h-3 w-3" />
          <span>Showing {filteredData.length} of {data.length} records</span>
          <button onClick={clearAllFilters} className="text-primary hover:underline ml-1">Clear filters</button>
        </div>
      )}
    </>
  );
}
